import apiClient from "./apiClient";
import { AxiosError } from "axios";
import serviceConfig from "config/serviceConfig";
import { setCookie } from "@utils/Cookie";
import getClientDeviceInfo from "@utils/getClientDeviceInfo";

type LoginCredType = "email" | "phone";

export const login = async (
	username: string,
	password: string,
	credType: LoginCredType = "email",
) => {
	const basicAuth = btoa(`${username}:${password}`);
	const deviceInfo = getClientDeviceInfo();

	try {
		const response = await apiClient.post(
			serviceConfig.routes.login,
			{},
			{
				headers: {
					CredType: credType,
					Authorization: `Basic ${basicAuth}`,
					"X-Device-Name": deviceInfo.deviceName,
					"X-Device-Model": deviceInfo.deviceModel,
					"X-Device-Firmware": deviceInfo.deviceFirmware,
					"X-App-Version": deviceInfo.appVersion,
					"X-App-Name": deviceInfo.appName,
				},
			},
		);

		return response.data;
	} catch (error) {
		if (error instanceof AxiosError) {
			throw new Error(error.response?.data?.message || "Failed to login");
		}
		throw new Error("An unknown error occurred during login");
	}
};

export const forgotPassword = async (
	credentials: string,
	credType: LoginCredType = "email",
) => {
	try {
		const response = await apiClient.post(
			serviceConfig.routes.forgotPassword,
			{},
			{
				headers: {
					Credentials: credentials,
					CredType: credType,
				},
			},
		);

		return response.data;
	} catch (error) {
		if (error instanceof AxiosError) {
			throw new Error(
				error.response?.data?.message || "Failed to request password reset",
			);
		}
		throw new Error("An unknown error occurred during password reset request");
	}
};

export const updatePassword = async ({
	email,
	phone,
	otp,
	password,
}: {
	email?: string;
	phone?: string;
	otp: string;
	password: string;
}) => {
	const credType = email ? "email" : "phone";
	const credentials = email ?? phone ?? "";

	try {
		const response = await apiClient.post(
			serviceConfig.routes.updatePassword,
			{},
			{
				headers: {
					Credentials: credentials,
					CredType: credType,
					OTP: otp,
					Password: password,
				},
			},
		);

		return response.data;
	} catch (error) {
		if (error instanceof AxiosError) {
			throw new Error(
				error.response?.data?.message || "Failed to update password",
			);
		}
		throw new Error("An unknown error occurred during password update");
	}
};

/**
 * Refresh Token Service
 */
export const refreshToken = async () => {
	try {
		const refreshToken = localStorage.getItem("refresh_token");
		if (!refreshToken) throw new Error("No refresh token available");

		const response = await apiClient.post(serviceConfig.routes.refreshToken, {
			refresh_token: refreshToken,
		});
		return response.data.access_token;
	} catch (error) {
		if (error instanceof AxiosError) {
			throw new Error(
				error.response?.data?.message || "Failed to refresh token",
			);
		}
		throw new Error("An unknown error occurred during token refresh");
	}
};

/**
 * Register Request Service
 */
export async function registerRequest({
	role_id,
	email,
	phone,
}: {
	role_id: number;
	email?: string;
	phone?: string;
}) {
	const credType = email ? "email" : "phone";
	const credentials = email ?? phone ?? "";
	const roleHeader = role_id === 3 ? "sender" : "carrier";

	try {
		// send in BODY, not in "headers" property
		const response = await apiClient.post(
			serviceConfig.routes.registerRequest,
			{}, // no body, all data in headers
			{
				headers: {
					RoleID: role_id,
					Credentials: credentials,
					CredType: credType,
					Role: roleHeader,
				},
			},
		);

		const setCookieHeader = (response as any).headers?.["set-cookie"];
		if (Array.isArray(setCookieHeader)) {
			const sessionCookie = setCookieHeader.find((c: string) =>
				c.startsWith("texsession="),
			);
			if (sessionCookie) {
				const cookieValue = sessionCookie.split(";")[0].split("=")[1];
				setCookie("texsession", cookieValue, 5);
			}
		}

		return response.data;
	} catch (error) {
		if (error instanceof AxiosError) {
			throw new Error(
				error.response?.data?.message || "Failed to send register request",
			);
		}
		throw new Error("An unknown error occurred during register request");
	}
}

/**
 * Validate OTP Service
 */
export const validateOtp = async ({
	otp,
	email,
	phone,
}: {
	otp: string;
	email?: string;
	phone?: string;
}) => {
	const credType = email ? "email" : "phone";
	const credentials = email ?? phone ?? "";

	try {
		const response = await apiClient.post(
			serviceConfig.routes.validateOtp,
			{},
			{
				headers: {
					OTP: otp,
					CredType: credType,
					Credentials: credentials,
				},
			},
		);
		return response.data;
	} catch (error) {
		if (error instanceof AxiosError) {
			throw new Error(
				error.response?.data?.message || "Failed to validate OTP",
			);
		}
		throw new Error("An unknown error occurred during OTP validation");
	}
};

/** Final register (email or phone) */
export const register = async ({
	email,
	phone,
	password,
	roleID,
	otp,
}: {
	email?: string;
	phone?: string;
	password: string;
	roleID: number;
	otp: string;
}) => {
	const credType = email ? "email" : "phone";
	const credentials = email ?? phone ?? "";
	const role = roleID === 3 ? "sender" : "carrier";

	try {
		const response = await apiClient.post(
			serviceConfig.routes.register,
			{
				// backend still expects body for these fields
				password,
				role_id: roleID,
				email: email ?? "",
				phone: phone ?? "",
				role, // <-- required in body
				username: "", // keep as before
			},
			{
				headers: {
					CredType: credType,
					Credentials: credentials,
					OTP: otp,
					RoleID: roleID,
					Role: role,
				},
			},
		);
		return response.data;
	} catch (error) {
		if (error instanceof AxiosError) {
			throw new Error(error.response?.data?.message || "Failed to register");
		}
		throw new Error("An unknown error occurred during registration");
	}
};

export const otpLoginRequest = async (phone: string) => {
	const deviceInfo = getClientDeviceInfo();
	await apiClient.post(
		serviceConfig.routes.otpLoginRequest, // e.g. '/auth/otp-login/request/'
		{},
		{
			headers: {
				CredType: "phone",
				Credentials: phone,
				Role: "user",
				RoleID: "3", // or whatever role you need
				"X-Device-Name": deviceInfo.deviceName,
				"X-Device-Model": deviceInfo.deviceModel,
				"X-Device-Firmware": deviceInfo.deviceFirmware,
				"X-App-Version": deviceInfo.appVersion,
				"X-App-Name": deviceInfo.appName,
			},
		},
	);
};

export const otpLoginValidate = async (phone: string, otp: string) => {
	const deviceInfo = getClientDeviceInfo();
	const response = await apiClient.post(
		serviceConfig.routes.otpLoginValidate, // e.g. '/auth/otp-login/validate/'
		{},
		{
			headers: {
				CredType: "phone",
				Credentials: phone,
				OTP: otp,
				Role: "user",
				"X-Device-Name": deviceInfo.deviceName,
				"X-Device-Model": deviceInfo.deviceModel,
				"X-Device-Firmware": deviceInfo.deviceFirmware,
				"X-App-Version": deviceInfo.appVersion,
				"X-App-Name": deviceInfo.appName,
			},
		},
	);
	return response.data;
};

export const googleAuth = async ({
	role,
	role_id,
	id_token,
}: {
	role: "sender" | "carrier";
	role_id: 3 | 4 | 5 | 6;
	id_token: string;
}) => {
	try {
		const response = await apiClient.post(
			"/auth/google/mobile/",
			{ role, role_id, id_token },
			{ headers: { "Content-Type": "application/json" } },
		);

		const authData = response.data?.data ?? response.data;
		return {
			...authData,
			exp: authData?.exp ?? authData?.expires_in ?? null,
		};
	} catch (error) {
		if (error instanceof AxiosError) {
			throw new Error(error.response?.data?.message || "Google login failed");
		}
		throw new Error("An unknown error occurred during Google login");
	}
};

export const saveNotificationToken = async (payload: {
	notification_token: string;
	device_type: "web";
}) => {
	try {
		const res = await apiClient.post(
			serviceConfig.routes.saveNotificationToken, // "/auth/save-notification-token/"
			payload,
		);
		return res.data;
	} catch (e) {
		if (e instanceof AxiosError) {
			throw new Error(
				e.response?.data?.message || "Failed to save notification token",
			);
		}
		throw new Error("Unknown error while saving notification token");
	}
};
